import { Controller } from '@hotwired/stimulus';
import {
  updateDocumentUserState,
  extractHiddenAnswerCommentHTML,
  restoreAnswerCommentHTML,
  shouldPreserveAnswerCommentInDom,
} from '../utils/case_experience_in_rails';

export default class extends Controller {
  static targets = ['answer'];
  static values = {
    documentId: Number,
    userId: Number,
    nodeUid: String,
    multipleSelect: Boolean,
    inactive: Boolean,
    nodeData: Object,
    graded: Boolean,
  };

  connect() {
    this.loadNodeData();
    if (this.answerTargets.length === 0) {
      return;
    }
    this.submitButtonContainer = this.findSubmitButtonContainer();
    if (this.submitButtonContainer) {
      const submitButton = this.submitButtonContainer.querySelector('button');
      if (submitButton) {
        this.submitButton = submitButton;
        this._handleSubmit = this.handleSubmit.bind(this);
        this.submitButton.addEventListener('click', this._handleSubmit);
      }
    }

    if (this.gradedValue) {
      this.disableSubmitButton();
      this.showGradedInfo();
      this.applyPostGradedDomState();
      this.lockSubmitButtonForGraded();
    } else if (!shouldPreserveAnswerCommentInDom()) {
      // Store answer comment HTML for restoration
      this.storeAnswerCommentHTML();
      this.updateSubmitButtonState();
    } else {
      this.updateSubmitButtonState();
    }
  }

  disableSubmitButton() {
    if (this.submitButton) {
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-disabled', 'true');
    }
  }

  lockSubmitButtonForGraded() {
    const slice = this.submitButtonContainer || this.findSubmitButtonContainer();
    if (!slice) return;
    slice.setAttribute('data-button-action-inactive-value', '2');
    const buttonAction = this.application?.getControllerForElementAndIdentifier(
      slice,
      'button-action',
    );
    if (buttonAction) {
      buttonAction.inactiveValue = 2;
    }
    this.disableSubmitButton();
  }

  applyPostGradedDomState() {
    const answerComment = this.element.parentElement?.querySelector('.answer_comment');
    if (answerComment) {
      answerComment.classList.remove('hidden');
    }
    this.answerTargets.forEach((target) => {
      const checkbox = target.querySelector('[role="checkbox"]');
      if (checkbox) {
        checkbox.setAttribute('aria-disabled', 'true');
        checkbox.tabIndex = -1;
      }
    });
    this.disableSubmitButton();
  }

  isSubmitButtonSlice(element) {
    if (!element?.classList?.contains('doc-button-slice')) return false;
    const name = (element.getAttribute('data-button-action-name-value') || '').trim().toLowerCase();
    return name === 'submit';
  }

  findSubmitButtonContainer() {
    const section = this.element.closest('.doc-section');
    let container = null;

    if (section) {
      const slices = section.querySelectorAll('.doc-button-slice');
      for (let i = 0; i < slices.length; i += 1) {
        const slice = slices[i];
        if (!this.isSubmitButtonSlice(slice)) continue;
        container = slice;
        break;
      }
    }

    if (!container) {
      container = this.findSubmitButtonAmongElementSiblings();
    }

    return container;
  }

  findSubmitButtonAmongElementSiblings() {
    let sib = this.element?.nextElementSibling;
    while (sib) {
      if (this.isSubmitButtonSlice(sib)) return sib;
      sib = sib.nextElementSibling;
    }
    sib = this.element?.previousElementSibling;
    while (sib) {
      if (this.isSubmitButtonSlice(sib)) return sib;
      sib = sib.previousElementSibling;
    }
    return null;
  }

  resolveSubmitButton() {
    if (this.submitButton) {
      return this.submitButton;
    }
    const container = this.findSubmitButtonContainer();
    return container ? container.querySelector('button') : null;
  }

  storeAnswerCommentHTML() {
    const result = extractHiddenAnswerCommentHTML(this.element);
    if (result) {
      this._answerCommentSibling = result.sibling;
      this.answerCommentHtml = result.html;
    } else {
      this._answerCommentSibling = null;
      this.answerCommentHtml = null;
    }
  }

  disconnect() {
    // Remove submit event listener if present
    if (this.submitButtonContainer && this.submitButton && this._handleSubmit) {
      this.submitButton.removeEventListener('click', this._handleSubmit);
    }
    this.submitButton = null;
    this._handleSubmit = null;
    this.submitButtonContainer = null;

    // Restore answer comment HTML on disconnect
    this.restoreAnswerCommentHTMLFromVariable();
  }

  restoreAnswerCommentHTMLFromVariable() {
    if (this.answerCommentHtml) {
      restoreAnswerCommentHTML(this.element, this.answerCommentHtml);
    }
  }

  async handleSubmit() {
    // On submit, mark as graded and show graded info dynamically
    try {
      // Save graded state to document user state
      const updatedAt = Date.now();
      const state = {};
      state[this.nodeUidValue] = {
        graded: { value: true, updatedAt },
      };
      await updateDocumentUserState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        nodeUid: this.nodeUidValue,
        body: { document_user_state: { state } },
      });
      // Show graded info in UI
      this.showGradedInfo();
      this.inactiveValue = true;
      this.gradedValue = true;
      this.applyPostGradedDomState();
      this.lockSubmitButtonForGraded();

      // Restore answer comment HTML after submit
      this.restoreAnswerCommentHTMLFromVariable();
    } catch (error) {
      console.error('Error in handleSubmit:', error);
    }
  }

  showGradedInfo() {
    // Use this._nodeData.content to determine correct answers
    const content = Array.isArray(this._nodeData?.content) ? this._nodeData.content : [];
    // Map of answerUid => isCorrect
    const correctMap = {};
    content.forEach((item) => {
      if (item && item.uid) {
        correctMap[item.uid] = !!item.answer;
      }
    });

    this.answerTargets.forEach((target) => {
      const answerUid = target.dataset.answerUid;
      const isCorrect = correctMap[answerUid] || false;
      const gradedInfo = target.querySelector('.doc-multiple-choice-answer-graded-info');

      if (gradedInfo) {
        gradedInfo.classList.remove('hidden', 'invisible');
        if (!isCorrect) {
          gradedInfo.classList.add('invisible');
        }
      }
    });
    // Show feedback message if present
    const feedback = this.element.querySelector('.graded-feedback');
    if (feedback) {
      feedback.classList.remove('hidden');
    }
    // Add graded class to ul
    const listEl = this.element.querySelector('.doc-multiple-choice');
    if (listEl) {
      listEl.classList.add('graded');
    }
  }

  handleKeydown(event) {
    // Support keyboard navigation (Enter or Space to select)
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.selectAnswer(event);
    }
  }

  async selectAnswer(event) {
    if (this.inactiveValue) return;

    const answerElement =
      event.currentTarget.closest('[data-multiple-choice-target="answer"]') || event.currentTarget;
    const answerUid = answerElement.dataset.answerUid;
    const isCurrentlyChecked = answerElement.classList.contains('checked');

    // If not multiple select, uncheck all other answers and update their state
    const updatedAt = Date.now();
    let liUids = [];
    let inactive_value = null;
    let nextSubmitDiv = null;
    let nextSubmitButton = null;

    const state = {};

    if (!this.multipleSelectValue && !isCurrentlyChecked) {
      this.answerTargets.forEach((target) => {
        const uid = target.dataset.answerUid;
        if (target !== answerElement) {
          target.classList.remove('checked');
          target.setAttribute('aria-checked', 'false');
          // Persist deselection in backend
          state[uid] = {};
          state[uid]['value'] = { value: false, updatedAt };
        }
      });
    }

    // Toggle the clicked answer
    const newCheckedState = !isCurrentlyChecked;
    answerElement.classList.toggle('checked', newCheckedState);
    answerElement.setAttribute('aria-checked', newCheckedState);

    // Always persist the clicked answer's state
    state[answerUid] = {};
    state[answerUid]['value'] = { value: newCheckedState, updatedAt };

    const currentSection = answerElement.closest('.doc-section');
    if (currentSection) {
      const questionNode = this.element;
      liUids = Array.from(
        questionNode.querySelectorAll('[data-multiple-choice-target="answer"][data-answer-uid]'),
      ).map((el) => el.dataset.answerUid);

      const anySelected = liUids.some((uid) => {
        const row = questionNode.querySelector(
          `[data-multiple-choice-target="answer"][data-answer-uid="${uid}"]`,
        );
        return row && row.classList.contains('checked');
      });

      nextSubmitButton = this.resolveSubmitButton();
      if (nextSubmitButton) {
        nextSubmitDiv = nextSubmitButton.closest('.doc-button-slice');

        if (nextSubmitDiv) {
          if (nextSubmitDiv?.dataset?.uid) {
            if (anySelected) {
              inactive_value = 1;
            } else {
              inactive_value = 2;
            }
            state[nextSubmitDiv.dataset.uid] = {};
            state[nextSubmitDiv.dataset.uid]['inactive'] = {
              value: inactive_value,
              updatedAt,
            };
          }
        }
      }
    }

    try {
      const response = await updateDocumentUserState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        nodeUid: this.nodeUidValue,
        body: {
          document_user_state: { state },
        },
      });

      if (!response) {
        throw new Error('Failed to save answer selection');
      }
    } catch (error) {
      console.error('Error selecting answer:', error);
      answerElement.classList.toggle('checked', isCurrentlyChecked);
      answerElement.setAttribute('aria-checked', isCurrentlyChecked);
    }

    // Update submit button state after selection
    this.updateSubmitButtonState();
  }

  updateSubmitButtonState() {
    if (this.gradedValue || this.inactiveValue) {
      this.lockSubmitButtonForGraded();
      if (this._answerCommentSibling) {
        this._answerCommentSibling.classList.remove('hidden');
      }
      return;
    }

    const nextSubmitButton = this.resolveSubmitButton();
    const submitSlice = nextSubmitButton?.closest('.doc-button-slice');
    const anySelected = this.answerTargets.some((target) => target.classList.contains('checked'));
    const inactive = anySelected ? 1 : 2;

    if (submitSlice && this.application) {
      submitSlice.setAttribute('data-button-action-inactive-value', String(inactive));
      const buttonAction = this.application.getControllerForElementAndIdentifier(
        submitSlice,
        'button-action',
      );
      if (buttonAction) {
        buttonAction.inactiveValue = inactive;
      } else if (nextSubmitButton) {
        nextSubmitButton.disabled = !anySelected;
        if (!anySelected) {
          nextSubmitButton.setAttribute('aria-disabled', 'true');
        } else {
          nextSubmitButton.removeAttribute('aria-disabled');
        }
      }
    } else if (nextSubmitButton) {
      nextSubmitButton.disabled = !anySelected;
      if (!anySelected) {
        nextSubmitButton.setAttribute('aria-disabled', 'true');
      } else {
        nextSubmitButton.removeAttribute('aria-disabled');
      }
    }

    // Remove hidden from the answer comment sibling if present
    if (this._answerCommentSibling) {
      this._answerCommentSibling.classList.remove('hidden');
    }
  }

  loadNodeData() {
    // Load nodeData from data attribute and remove it from DOM for security
    const nodeDataAttr = this.element.getAttribute('data-multiple-choice-node-data-value');
    if (nodeDataAttr) {
      try {
        this._nodeData = JSON.parse(nodeDataAttr);
      } catch (error) {
        console.error('Error parsing nodeData JSON:', error);
        this._nodeData = {};
      }
      this.element.removeAttribute('data-multiple-choice-node-data-value');
    } else {
      this._nodeData = {};
    }
  }
}
